import { useState, useEffect } from 'react';
import { Card, Loader, CheckboxInput, Input, Button } from '@nearst/ui';
import ReactSelect from 'react-select';
import { StepNumber } from './StepNumber';
import { OAuthConnection } from './OAuthConnection';
import { PlugAndPlayConnection } from './PlugAndPlayConnection';
import { PartnerLeadConnection } from './PartnerLeadConnection';
import { ConnectionStatus } from './ConnectionStatus';
import { CurrentConnectionInfo } from './CurrentConnectionInfo';

import { useConnectionContext } from '../context/ConnectionContext';
import { getDisplayConnection, getFilteredPosList, shouldShowLoading, findInventorySource } from '../utils/shopUtils';
import { CONNECTION_KIND } from '../constants';
import shopApi from '@services/shop';
import styles from '../UnifyOnboarding.module.scss';

/**
 * POS Connection
 * Handles stock inventory and product data connections
 */
export const POSConnection = ({ isActive, isCompleted, businessDetailsCompleted }) => {
	// Get all state from context
	const {
		stock,
		product,
		posList,
		posLoading,
		includeProductData,
		loadingInventorySources,
		currentShop,
		onConnectPOS,
		onIncludeProductDataChange,
		onCloseEdit,
		onShopRefresh,
		setError,
	} = useConnectionContext();

	// Custom POS state
	const [showCustomPosInput, setShowCustomPosInput] = useState(false);
	const [customPosName, setCustomPosName] = useState('');
	const [savingCustomPos, setSavingCustomPos] = useState(false);

	// Initialize custom POS name from shop data
	useEffect(() => {
		if (currentShop?.requestedPosIntegration) {
			setCustomPosName(currentShop.requestedPosIntegration);
		}
	}, [currentShop?.requestedPosIntegration]);

	// Handle custom POS submission
	const handleCustomPosSubmit = async () => {
		if (!customPosName.trim() || savingCustomPos) return;

		setSavingCustomPos(true);
		try {
			// Save to shop
			await shopApi.updateMyShop(currentShop.id, {
				requestedPosIntegration: customPosName.trim()
			});

			// Refresh shop data
			await onShopRefresh();

			// Close input UI
			setShowCustomPosInput(false);

			// Enter editing mode and select email provider
			stock.setEditing(true);
			stock.setEditingPOS('email');

		} catch (error) {
			console.error('Failed to save custom POS request:', error);
			setError('Failed to save custom POS. Please try again.');
		} finally {
			setSavingCustomPos(false);
		}
	};

	const isInitialSetup = !isCompleted && !stock.editing && !product.editing;
	const isEditingProduct = product.editing;

	const display = getDisplayConnection(isEditingProduct, stock, product);
	const existingProductSource = findInventorySource(currentShop, CONNECTION_KIND.PRODUCT);
	const filteredPosList = getFilteredPosList(posList, isEditingProduct, isInitialSetup);
	const showLoading = shouldShowLoading(businessDetailsCompleted, stock.editing, product.editing, loadingInventorySources, isCompleted, stock.selectedPOS);

	// Only show polling message for spinner-type connectors
	const shouldShowPollingMessage =
		(stock.pollingForConnection && stock.provider?.connect?.submitType === 'spinner') ||
		(product.pollingForConnection && product.provider?.connect?.submitType === 'spinner');



	return (
		<Card className={styles.stepCard}>
			<div className={styles.stepHeader}>
				<StepNumber number={3} isActive={isActive} isCompleted={isCompleted || stock.editing || product.editing} />
				<h3 className={styles.stepTitle}>Connect your inventory management system</h3>
			</div>
			{showLoading ? (
				<div className={`${styles.stepContent} ${styles.stepLoadingContainer}`}>
					<Loader size={32} />
				</div>
			) : !isCompleted || stock.editing || product.editing ? (
				<div className={styles.stepContent}>
					{(stock.editing || product.editing) && (
						<div className={styles.editHeader}>
							<p className={styles.helperText}>
								{stock.editing ? 'Updating stock inventory connection' : 'Updating product data connection'}
							</p>
							<button className={styles.linkButton} onClick={onCloseEdit}>
								Cancel
							</button>
						</div>
					)}

					{stock.editing && stock.source && (
						<CurrentConnectionInfo
							source={stock.source}
							provider={stock.provider}
							kind="stock"
						/>
					)}

					{product.editing && existingProductSource && existingProductSource.pointOfSale && (
						<CurrentConnectionInfo
							source={existingProductSource}
							provider={product.provider}
							kind="product"
						/>
					)}

					{shouldShowPollingMessage && (
						<div className={styles.pollingMessage} data-cy="polling-message">
							<p>Checking connection status...</p>
							<p className={styles.helperText}>
								This may take a few moments. We&apos;re waiting for your first inventory upload.
							</p>
						</div>
					)}

					{stock.connectionWarning && (
						<div className={styles.warning}>
							{stock.connectionWarning}
						</div>
					)}

					{product.connectionWarning && (
						<div className={styles.warning}>
							{product.connectionWarning}
						</div>
					)}

					{businessDetailsCompleted && (
						<div className={styles.mobileOnlyInfo}>
							<p>
								<strong>Best done on desktop:</strong> For easier setup, complete this step on the computer where your POS system runs.
							</p>
						</div>
					)}

					<div className={styles.selectWithLink}>
						<label className={styles.selectLabel}>
							{isEditingProduct ? "Select a Product Data Source" : "Select an Inventory Management System"}
						</label>
						<div className={styles.selectRow}>
							<ReactSelect
								id="pos-select"
								placeholder="Select a source..."
								isDisabled={!businessDetailsCompleted || posLoading}
								isLoading={posLoading}
								value={filteredPosList.find(pos => pos.value === display.pos) || null}
								onChange={(selectedOption) => display.posChange(selectedOption ? selectedOption.value : '')}
								options={filteredPosList}
								isClearable={false}
								isSearchable={true}
								components={{
									IndicatorSeparator: () => null
								}}
								styles={{
									control: (provided, state) => ({
										...provided,
										borderRadius: '0.25rem',
										borderColor: state.isFocused ? '#6366f1' : '#d1d5db',
										boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
										'&:hover': {
											borderColor: state.isFocused ? '#6366f1' : '#9ca3af'
										}
									}),
									container: (provided) => ({
										...provided,
										width: '16rem',
										maxWidth: '100%'
									})
								}}
								data-cy="pos-select"
							/>
							{isInitialSetup && !showCustomPosInput && businessDetailsCompleted && (
								<button
									type="button"
									className={styles.linkButton}
									onClick={() => setShowCustomPosInput(true)}
									disabled={posLoading || savingCustomPos}
								>
									Can&apos;t find your Inventory management system?
								</button>
							)}
						</div>
					</div>

					{/* Custom POS Input Form - Only show during initial setup */}
					{isInitialSetup && showCustomPosInput && (
						<div className={styles.customPosPrompt}>
							{showCustomPosInput && (
								<div className={styles.customPosInput}>
									<label htmlFor="custom-pos-name">
										Enter your inventory management system name:
									</label>
									<Input
										id="custom-pos-name"
										value={customPosName}
										onChange={(e) => setCustomPosName(e.target.value)}
										placeholder="e.g., MyPOS System"
										disabled={savingCustomPos}
									/>
									<div className={styles.customPosButtons}>
										<Button
											variant="primary"
											onClick={handleCustomPosSubmit}
											disabled={!customPosName.trim() || savingCustomPos}
										>
											{savingCustomPos ? 'Saving...' : 'Submit'}
										</Button>
										<Button
											variant="secondary"
											onClick={() => {
												setShowCustomPosInput(false);
												setCustomPosName(currentShop?.requestedPosIntegration || '');
											}}
											disabled={savingCustomPos}
										>
											Cancel
										</Button>
									</div>
								</div>
							)}
						</div>
					)}

					{isInitialSetup && !includeProductData && display.provider && display.provider.kinds?.includes('product') && (
						<div className={styles.productDataPrompt}>
							<h4>Product data connection (optional)</h4>
							<p className={styles.helperText}>
								Connect product information (titles, descriptions, images) through your point-of-sale system to manage your
								product catalogue automatically. You can skip this and add it later if you prefer.
							</p>
							<CheckboxInput
								label="Yes, also sync product information"
								checked={includeProductData}
								onChange={(e) => onIncludeProductDataChange(e.target.checked)}
								disabled={posLoading}
							/>
						</div>
					)}

					{isInitialSetup && includeProductData && display.provider && display.provider.kinds?.includes('product') && (
						<div className={styles.productDataEnabled}>
							<p>
								<strong>✓ Product data sync enabled</strong>
							</p>
							<p className={styles.helperText}>Product information will be synced along with stock levels.</p>
						</div>
					)}

					<OAuthConnection
						provider={display.provider}
						onConnect={() => onConnectPOS(isEditingProduct ? 'product' : 'stock')}
						isLoading={posLoading}
						isDisabled={!businessDetailsCompleted}
					/>

					<PlugAndPlayConnection
						provider={display.provider}
						formData={display.formData}
						formErrors={display.formErrors}
						sessionId={display.sessionId}
						isLoading={posLoading}
						isDisabled={!businessDetailsCompleted}
						isPolling={display.isPolling}
						uploadKey={currentShop?.uploadKey}
						hasExistingCredentials={(isEditingProduct ? existingProductSource : currentShop?.stockInventorySource)?.hasCredentials}
						customPosName={currentShop?.requestedPosIntegration}
						onFormChange={display.formChange}
						onConnect={() => onConnectPOS(isEditingProduct ? 'product' : 'stock')}
					/>

					<PartnerLeadConnection
						provider={display.provider}
						onConnect={() => onConnectPOS(isEditingProduct ? 'product' : 'stock')}
						isLoading={posLoading}
						isDisabled={!businessDetailsCompleted}
					/>

					{display.pos === 'other' && (
						<p className={styles.helperText}>
							<a
								href="https://support.near.st/en/articles/7950090-my-pos-is-not-on-the-list-of-current-integrations"
								target="_blank"
								rel="noreferrer"
							>
								Why can&apos;t I find my POS on the list of current integrations?
							</a>
						</p>
					)}
				</div>
			) : (
				<div className={styles.stepContent}>
					<ConnectionStatus
						source={stock.source}
						provider={stock.provider}
						kind="stock"
						onEdit={stock.onEdit}
					/>

					<ConnectionStatus
						source={existingProductSource}
						provider={product.provider}
						kind="product"
						onEdit={product.onEdit}
					/>
				</div>
			)}
		</Card>
	);
};
